import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Check, X, AlertCircle, Info } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { devLog, devError } from "@/utils/devLog";
import { Skeleton } from "@/components/ui/skeleton";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useAuth } from "@/providers/AuthProvider";

const RESERVED_USERNAMES = [
  "admin", "login", "dashboard", "create", "showee", "signup", "signin", 
  "profile", "settings", "help", "support", "course", "courses", "pricing",
  "about", "contact", "terms", "privacy", "legal", "faq"
];

interface ProfileData {
  username: string | null;
  role?: string;
  full_name?: string | null;
}

const UsernameSetup = () => {
  console.log("[UsernameSetup] component mounted");
  
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [username, setUsername] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validation, setValidation] = useState({
    isValid: false,
    lengthValid: false,
    formatValid: false,
    notReserved: false,
    isAvailable: false,
  });
  const [navigationAttempted, setNavigationAttempted] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitAttempts, setSubmitAttempts] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const sessionRef = useRef<any>(null);
  const usernameInputRef = useRef<HTMLInputElement>(null);
  const userIdRef = useRef<string | null>(null);
  const submissionInProgressRef = useRef<boolean>(false);
  
  const { user, initialSessionProcessed } = useAuth();
  const { profile, isLoading: profileLoading, refetchProfile } = useUserProfile();

  useEffect(() => {
    const savedUserId = localStorage.getItem('user_id');
    const savedRole = localStorage.getItem('user_role');
    
    if (savedUserId) {
      devLog("[UsernameSetup] Found saved user ID in localStorage:", savedUserId);
      userIdRef.current = savedUserId;
    }
    
    if (savedRole === 'creator') {
      devLog("[UsernameSetup] Found creator role in localStorage");
    }
  }, []);

  const { data: session, isLoading: sessionLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      console.log("[UsernameSetup] fetching current session");
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error("[UsernameSetup] Error fetching session:", error);
        return null;
      }
      console.log("[UsernameSetup] got session:", !!data.session, "with user ID:", data.session?.user?.id);
      
      if (data.session?.user?.id) {
        userIdRef.current = data.session.user.id;
        localStorage.setItem('user_id', data.session.user.id);
      }
      
      return data.session;
    },
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 3,
  });

  useEffect(() => {
    if (session) {
      console.log("[UsernameSetup] Storing session in ref:", !!session);
      sessionRef.current = session;
    }
  }, [session]);

  const { data: profileData, isLoading: profileDataLoading, isError: profileError } = useQuery<ProfileData | null>({
    queryKey: ['userProfile', session?.user?.id, userIdRef.current],
    queryFn: async () => {
      const userId = session?.user?.id || userIdRef.current;
      if (!userId) return null;
      
      console.log("[UsernameSetup] fetching existing profile...");
      console.log("[UsernameSetup] current user id:", userId);

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('username, role')
          .eq('id', userId)
          .maybeSingle();
        
        if (error) {
          console.error("[UsernameSetup] Error fetching profile:", error);
          throw error;
        }
        
        console.log("[UsernameSetup] existing username:", data?.username);
        
        if (data?.role) {
          localStorage.setItem('user_role', data.role);
        }
        
        return data as ProfileData;
      } catch (error) {
        console.error("[UsernameSetup] Profile fetch error:", error);
        return null;
      }
    },
    enabled: !!(session?.user?.id || userIdRef.current),
    staleTime: 0,
    retry: 3,
  });

  useEffect(() => {
    devLog("[UsernameSetup] Profile state:", {
      sessionLoading,
      profileLoading: profileDataLoading,
      hasUsername: !!profileData?.username,
      error: profileError,
      navigationAttempted,
      sessionExists: !!session,
      userId: session?.user?.id || userIdRef.current
    });

    if (!sessionLoading && !profileDataLoading && profileData?.username && !navigationAttempted) {
      devLog("[UsernameSetup] Username exists, redirecting to dashboard");
      setNavigationAttempted(true);
      navigate("/dashboard/creator", { replace: true });
    }
  }, [profileDataLoading, profileData, profileError, navigate, navigationAttempted, session, sessionLoading]);

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "");
    console.log("[UsernameSetup] Input changed:", value);
    setUsername(value);
  };

  useEffect(() => {
    devLog("[UsernameSetup] Current username check:", username);

    const lengthValid = username.length >= 3 && username.length <= 20;
    devLog("[UsernameSetup] Length valid:", lengthValid);
    
    const formatValid = /^[a-z0-9_]+$/.test(username);
    devLog("[UsernameSetup] Format valid:", formatValid);
    
    const notReserved = !RESERVED_USERNAMES.includes(username);
    devLog("[UsernameSetup] Not reserved:", notReserved);
    
    if (lengthValid && formatValid && notReserved && username.length > 0) {
      setIsChecking(true);
      
      const checkUsernameAvailability = setTimeout(async () => {
        try {
          devLog("[UsernameSetup] ➡️ Checking username availability for:", username);
          const { data, error } = await supabase
            .from('profiles')
            .select('username')
            .filter('username', 'eq', username)
            .maybeSingle();
          
          if (error) {
            devError("[UsernameSetup] ❌ Error checking username:", error);
            toast.error("Error checking username availability");
          }
          
          const isAvailable = !data;
          devLog("[UsernameSetup] Username availability result:", { isAvailable, data });
          
          setValidation(prev => ({
            ...prev,
            lengthValid,
            formatValid,
            notReserved,
            isAvailable,
            isValid: isAvailable && lengthValid && formatValid && notReserved
          }));
        } catch (error) {
          devError("[UsernameSetup] ❌ Unexpected error:", error);
        } finally {
          setIsChecking(false);
        }
      }, 500);
      
      return () => clearTimeout(checkUsernameAvailability);
    } else {
      setValidation({
        lengthValid,
        formatValid,
        notReserved,
        isAvailable: false,
        isValid: false
      });
    }
  }, [username]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formSubmitted || isLoading || submissionInProgressRef.current) {
      console.log("[UsernameSetup] Form already submitted or loading, preventing duplicate submission");
      return;
    }
    
    console.log("[UsernameSetup] handleFormSubmit called, attempt #", submitAttempts + 1);
    setSubmitAttempts(prev => prev + 1);
    submissionInProgressRef.current = true;
    
    const userId = user?.id || userIdRef.current;
    console.log("[UsernameSetup] User ID for submission:", userId);
    
    if (!validation.isValid || !userId) {
      console.log("[UsernameSetup] Validation failed or no session, can't submit", {
        validationState: validation,
        userId,
      });
      
      if (!userId) {
        toast.error("Your session has expired. Please sign in again.");
        setTimeout(() => navigate('/login', { replace: true }), 1000);
      }
      
      submissionInProgressRef.current = false;
      return;
    }
    
    console.log("[UsernameSetup] Form submission starting for username:", username, "for user ID:", userId);
    setIsLoading(true);
    setFormSubmitted(true);
    
    try {
      console.log("[UsernameSetup] Updating profile with username:", username);
      
      const { data, error } = await supabase
        .from("profiles")
        .update({ username: username })
        .eq("id", userId)
        .select();
    
      console.log("[UsernameSetup] UPDATE result:", { data, error });
    
      if (error) {
        devError("[UsernameSetup] ❌ Error saving username:", error);
        toast.error("Failed to save username. Please try again.");
        setIsLoading(false);
        setFormSubmitted(false);
        submissionInProgressRef.current = false;
        return;
      }
      
      await supabase.auth.refreshSession();
      
      await queryClient.invalidateQueries();
      
      await queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      await queryClient.invalidateQueries({ queryKey: ['userRoleAndUsername'] });
      
      console.log("[UsernameSetup] Waiting for profile data to refresh");
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['userProfileComplete'], exact: true }),
        queryClient.refetchQueries({ queryKey: ['userRoleAndUsername'], exact: true })
      ]);
      
      if (refetchProfile) {
        console.log("[UsernameSetup] Explicitly refetching profile");
        await refetchProfile();
      }
      
      console.log("[UsernameSetup] Username saved successfully");
      toast.success("Username saved successfully");
      
      setNavigationAttempted(true);
      
      localStorage.setItem('username_setup_complete', 'true');
      localStorage.setItem('username_setup_user_id', userId);
      
      console.log("[UsernameSetup] Now navigating to dashboard with fresh data");
      navigate("/dashboard/creator", { replace: true });
    } catch (error: any) {
      devError("[UsernameSetup] ❌ Error:", error);
      toast.error(error.message || "Failed to save username. Please try again.");
      setFormSubmitted(false);
      setIsLoading(false);
      submissionInProgressRef.current = false;
    }
  };

  const handleButtonClick = useCallback(() => {
    console.log("[UsernameSetup] Button clicked directly");
    
    if (submissionInProgressRef.current) {
      console.log("[UsernameSetup] Submission already in progress, preventing duplicate click");
      return;
    }
    
    if (!formRef.current) {
      console.error("[UsernameSetup] Form ref not found");
      return;
    }
    
    const userId = user?.id || (sessionRef.current?.user?.id) || localStorage.getItem('user_id');
    
    console.log("[UsernameSetup] Button conditions:", {
      isValid: validation.isValid,
      isLoading,
      isChecking,
      formSubmitted,
      hasUserId: !!userId
    });
    
    if (!userId) {
      console.log("[UsernameSetup] No user ID, fetching fresh session");
      
      supabase.auth.getSession().then(({ data }) => {
        if (data.session?.user?.id) {
          userIdRef.current = data.session.user.id;
          sessionRef.current = data.session;
          localStorage.setItem('user_id', data.session.user.id);
          
          if (validation.isValid && !isLoading && !isChecking && !formSubmitted) {
            console.log("[UsernameSetup] Button conditions met, triggering form submission");
            formRef.current?.dispatchEvent(
              new Event("submit", { cancelable: true, bubbles: true })
            );
          }
        } else {
          console.log("[UsernameSetup] Still no valid session");
          toast.error("Your session has expired. Please sign in again.");
          navigate('/login', { replace: true });
        }
      });
      return;
    }
    
    if (validation.isValid && !isLoading && !isChecking && !formSubmitted) {
      console.log("[UsernameSetup] Button conditions met, triggering form submission");
      formRef.current.dispatchEvent(
        new Event("submit", { cancelable: true, bubbles: true })
      );
    }
  }, [validation.isValid, isLoading, isChecking, formSubmitted, navigate]);

  useEffect(() => {
    if (usernameInputRef.current && !sessionLoading && !profileDataLoading) {
      usernameInputRef.current.focus();
    }
  }, [sessionLoading, profileDataLoading]);

  if (sessionLoading || profileDataLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <main className="flex-1 flex items-center justify-center py-12 px-4">
          <Card className="w-full max-w-md">
            <CardHeader className="space-y-2">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-full" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (profileError) {
    return (
      <div className="min-h-screen flex flex-col">
        <main className="flex-1 flex items-center justify-center py-12 px-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-red-600 flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Error Loading Profile
              </CardTitle>
              <CardDescription>
                Unable to load your profile information. Please try refreshing the page.
              </CardDescription>
            </CardHeader>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 flex items-center justify-center py-12 px-4 bg-primary-50">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center space-y-2">
            <CardTitle className="text-2xl font-heading">Choose your public profile name</CardTitle>
            <CardDescription>
              This name will appear in your profile link: <span className="font-mono font-medium">showee.com/{username || "yourname"}</span>
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form id="username-form" ref={formRef} onSubmit={handleFormSubmit} className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="username">Username</Label>
                  {isChecking && (
                    <span className="text-xs text-muted-foreground animate-pulse">Checking availability...</span>
                  )}
                </div>
                
                <div className="relative">
                  <Input
                    ref={usernameInputRef}
                    id="username"
                    value={username}
                    onChange={handleUsernameChange}
                    placeholder="e.g. johndoe"
                    className="pr-10"
                    autoComplete="off"
                    disabled={isLoading || formSubmitted}
                  />
                  
                  {username && !isChecking && (
                    <div className="absolute inset-y-0 right-3 flex items-center">
                      {validation.isValid ? (
                        <Check className="h-5 w-5 text-green-500" />
                      ) : (
                        <X className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                  )}
                </div>
                
                <div className="space-y-2 text-sm">
                  <ValidationItem 
                    isValid={validation.lengthValid || username.length === 0} 
                    text="Between 3-20 characters" 
                  />
                  <ValidationItem 
                    isValid={validation.formatValid || username.length === 0} 
                    text="Only lowercase letters, numbers, and underscores" 
                  />
                  <ValidationItem 
                    isValid={validation.notReserved || username.length === 0} 
                    text="Not a reserved name" 
                  />
                  {username.length > 0 && !isChecking && (
                    <ValidationItem 
                      isValid={validation.isAvailable} 
                      text={validation.isAvailable ? "Username is available" : "Username is already taken"} 
                    />
                  )}
                </div>
              </div>
            </form>
              
            <Button 
              type="button"
              className="w-full mt-4" 
              disabled={!validation.isValid || isLoading || isChecking || formSubmitted || submissionInProgressRef.current}
              onClick={handleButtonClick}
            >
              {isLoading ? "Saving..." : formSubmitted ? "Redirecting..." : "Continue to Dashboard"}
            </Button>
          </CardContent>
          
          <CardFooter>
            <div className="flex items-center gap-2 w-full justify-center text-sm text-muted-foreground">
              <Info className="h-4 w-4" />
              <p>You can change your username later in settings</p>
            </div>
          </CardFooter>
        </Card>
      </main>
    </div>
  );
};

const ValidationItem = ({ isValid, text }: { isValid: boolean; text: string }) => (
  <div className="flex items-center gap-2">
    {isValid ? (
      <Check className="h-4 w-4 text-green-500" />
    ) : (
      <X className="h-4 w-4 text-red-500" />
    )}
    <span className={isValid ? "text-green-700" : "text-red-700"}>
      {text}
    </span>
  </div>
);

export default UsernameSetup;
